import { FC } from 'react';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityHeroBlock } from '@lib/sanity/types';

type HeroBlockProps = SanityHeroBlock & {
  onCtaClick?: () => void;
};

const HeroBlock: FC<HeroBlockProps> = ({
  heading,
  subheading,
  backgroundImage,
  callToActionText,
  subtextBelow,
  isFullScreenHeight,
  onCtaClick
}) => {
  const backgroundImageUrl = backgroundImage
    ? buildSanityImageUrl(backgroundImage).width(1920).quality(80).url()
    : null;

  return (
    <section
      className="relative flex flex-col items-center px-6 pt-24 pb-20 text-center"
      style={{
        minHeight: isFullScreenHeight ? '100vh' : '420px',
        ...(backgroundImageUrl
          ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }
          : {})
      }}
    >
      {/* Decorative accent line */}
      <div className="mb-8 h-1 w-12 rounded-full bg-primary-600" />

      <h1
        className="mb-5 max-w-lg text-center font-heading leading-[1.05] font-black text-neutral-800 tracking-tight"
        style={{
          fontSize: 'clamp(2.5rem, 6vw, 3.75rem)'
        }}
      >
        {heading}
      </h1>

      {subheading && (
        <p className="mb-10 max-w-md font-body text-base text-neutral-600 leading-relaxed">
          {subheading}
        </p>
      )}

      {callToActionText && (
        <button
          type="button"
          onClick={onCtaClick}
          className="group relative overflow-hidden px-10 py-4 text-sm font-semibold tracking-wide text-white font-body transition-all duration-300 hover:shadow-lg bg-primary-600 rounded"
        >
          <span className="relative z-10">{callToActionText}</span>
          <div className="absolute inset-0 translate-y-full transition-transform duration-300 group-hover:translate-y-0 bg-primary-700" />
        </button>
      )}

      {subtextBelow && (
        <p className="mt-5 font-heading italic text-sm text-neutral-400">
          {subtextBelow}
        </p>
      )}
    </section>
  );
};

export default HeroBlock;
